import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useRecoilState, useRecoilValue } from 'recoil';

import { updatePractice } from 'api/Practice';

import { useCurrentPractice } from 'lib/hooks/queries/practice/useCurrentPractice';

import ConsentModal from 'components/practice/Settings/components/Account/components/TechnologyConsent/ConsentModal';
import practiceState from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Button/Button';
import Divider from 'components/shared/Divider/Divider';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

import OperationalSettings from './components/OperationalSettings';
import PracticeIdentity from './components/PracticeIdentity';
import PracticeLocation from './components/PracticeLocation';
import PracticeTimezone from './components/PracticeTimezone';
import PracticeWorkingDays from './components/PracticeWorkingDays';
import formInitialValues from './lib/formInitialValues';
import formValidationSchema from './lib/formValidationSchema';
import { getUpdateFields, hasAddressChanged, hasPracticeFieldsChanged } from './lib/helpers';

const ReinitializeForm = ({ resetForm }) => {
  const practice = useRecoilValue(practiceState.currentPractice);
  useEffect(() => {
    resetForm({ values: formInitialValues(practice) });
  }, [practice]);
  return null;
};

const FormTracker = ({ dirty, setHasUnsavedChanges }) => {
  useEffect(() => {
    setHasUnsavedChanges(dirty);
  }, [dirty, setHasUnsavedChanges]);

  return null;
};

const PracticeDetails = () => {
  const addressRef = useRef();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [practice, setPractice] = useRecoilState(practiceState.currentPractice);
  const [_hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showTechnologyModal, setShowTechnologyModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const pendingNavigation = useRef(null);

  const { data, isLoading } = useCurrentPractice();
  const formikRef = useRef();

  useEffect(() => {
    setPractice((prev) => ({
      ...prev,
      ...(data?.practice || {})
    }));
  }, [data]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const initialValues = formInitialValues(practice);
    const addressChanged = hasAddressChanged(values?.address, initialValues?.address);
    const practiceChanged = hasPracticeFieldsChanged(values, initialValues);

    if (!addressChanged && !practiceChanged) {
      setSubmitting(false);
      return;
    }

    let updateSuccessful = true;

    // Only update address if it has changed
    if (addressChanged) await addressRef.current?.updateAddress();

    // Only update practice if fields have changed
    if (practiceChanged) {
      const updateFields = getUpdateFields(values, initialValues);

      const res = await updatePractice(navigate, {
        practice_id: practice.id,
        fields: updateFields
      });

      if (res.code !== 0) {
        updateSuccessful = false;
      }
    }

    if (updateSuccessful) {
      await queryClient.invalidateQueries(['currentPractice']);

      setHasUnsavedChanges(false);

      showAlert({
        title: 'Practice details updated successfully',
        color: 'success'
      });
    }

    setSubmitting(false);
  };

  if (isLoading && !practice)
    return <Skeleton count={4} height={40} containerClassName="w-full mb-10" />;

  return (
    <div className="h-full w-full">
      <Formik
        key={isLoading ? 'loading' : 'loaded'}
        innerRef={formikRef}
        initialValues={formInitialValues(practice)}
        validationSchema={formValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}>
        {({ dirty, resetForm, isSubmitting, setFieldValue }) => (
          <Form className="flex h-full w-full flex-col">
            <ReinitializeForm values={practice} resetForm={resetForm} />
            <FormTracker dirty={dirty} setHasUnsavedChanges={setHasUnsavedChanges} />
            <div className="flex flex-1 flex-col overflow-y-auto pb-8">
              <div className="flex w-[770px] flex-col gap-6 self-center p-4">
                <PracticeIdentity />
                <Divider />
                <PracticeLocation ref={addressRef} />
                <Divider />
                <PracticeTimezone />
                <Divider />
                <PracticeWorkingDays />
                <Divider />
                <OperationalSettings setShowTechnologyModal={setShowTechnologyModal} />
              </div>
            </div>
            {dirty && (
              <div className="flex items-center justify-end gap-2 border-0 border-t border-solid border-neutral-200 bg-white p-3">
                <Button
                  text="Cancel"
                  variant="tertiary"
                  color="neutral"
                  size="small"
                  onClick={() => setShowUnsavedModal(true)}
                  disabled={isSubmitting}
                />
                <Button
                  text="Update"
                  color="primary"
                  size="small"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            )}
            <ConsentModal
              showTechnologyModal={showTechnologyModal}
              hideTechnologyModal={() => setShowTechnologyModal(false)}
              setPractice={setPractice}
              practice={practice}
              updateSurcharge={() => setFieldValue('surchargeEnabled', true)}
            />
            <Confirm
              type="unsaved-changes"
              isOpen={showUnsavedModal}
              handleClose={() => setShowUnsavedModal(false)}
              title="Discard unsaved changes?"
              message="Your unsaved changes will be discarded."
              footerActions={{
                keepEditing: true,
                primary: {
                  text: 'Discard',
                  onClick: () => {
                    resetForm();
                    setHasUnsavedChanges(false);
                    setShowUnsavedModal(false);

                    pendingNavigation.current?.retry?.();
                  },
                  color: 'danger'
                },
                secondary: null
              }}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default withErrorBoundary(PracticeDetails);
